import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Bouton } from './composants/Bouton';
import { Message } from './composants/Message';
import { PageConnexion } from './pages/Connexion';
import { Lien, naviguer, segmenter, useChemin } from './routage/routeur';
import { useSession } from './session/ContexteSession';
import { accueilDe, estProfessionnelSeul, type Session } from './session/session';

/**
 * Aiguillage des routes et des surfaces.
 *
 * Trois surfaces se partagent l'application : le portail du professionnel, le
 * poste agent et l'écran de signature. Elles ne diffèrent pas par leurs droits —
 * l'API les tranche à chaque requête — mais par leur traitement : une colonne
 * sans jargon d'un côté, densité maximale de l'autre.
 *
 * **Chaque surface est un fragment chargé à la demande.** Un patron de pirogue
 * sur une liaison 3G n'a aucune raison de télécharger la file d'instruction des
 * administrations ni l'écran de signature ministérielle : il ne les verra
 * jamais. Le découpage suit donc la frontière des publics, pas celle des pages.
 *
 * Sans session, tout mène à l'écran de connexion, qui reste dans le fragment
 * principal : c'est la seule vue que tout le monde traverse.
 */

const SurfacePortail = lazy(async () => {
  const module = await import('./surfaces/SurfacePortail');
  return { default: module.SurfacePortail };
});

const SurfaceAgent = lazy(async () => {
  const module = await import('./surfaces/SurfaceAgent');
  return { default: module.SurfaceAgent };
});

/**
 * Chargé à la demande, comme les surfaces : cet écran ne s'impose qu'une fois
 * dans la vie d'un compte, et il tire la politique de mot de passe de
 * `@sygap/core`. Le mettre dans le fragment principal ferait payer ~4 Ko à
 * chaque première visite, y compris à qui ne le verra jamais.
 */
const PageMotDePasse = lazy(async () => {
  const module = await import('./pages/MotDePasse');
  return { default: module.PageMotDePasse };
});

export function App() {
  const { session } = useSession();
  const chemin = useChemin();

  useEffect(() => {
    if (session === null) return;
    if (chemin === '/' || chemin === '') {
      naviguer(accueilDe(session), { remplacer: true });
    }
  }, [session, chemin]);

  if (session === null) {
    return <PageConnexion />;
  }

  // Mot de passe provisoire : rien d'autre n'est atteignable tant qu'il n'a pas
  // changé. Le laisser en service, c'est laisser un compte dont l'administrateur
  // qui l'a ouvert connaît encore le mot de passe.
  if (session.changementRequis) {
    return <ChangementImpose />;
  }

  const [racine] = segmenter(chemin);

  return (
    <AttenteFragment>
      {estSurfacePortail(racine, session) ? (
        <SurfacePortail chemin={chemin} accueil={accueilDe(session)} />
      ) : (
        <SurfaceAgent chemin={chemin} />
      )}
    </AttenteFragment>
  );
}

/**
 * Écran de changement imposé, hors de toute surface.
 *
 * Sans navigation : la proposer reviendrait à offrir la sortie que cet écran a
 * précisément pour objet de fermer. Seule la déconnexion reste offerte — un
 * agent qui renonce doit pouvoir libérer le poste.
 */
function ChangementImpose() {
  const { fermer } = useSession();
  return (
    <main className="sygap-changement-impose" id="contenu">
      <AttenteFragment>
        <PageMotDePasse impose />
      </AttenteFragment>
      <Bouton onClick={fermer}>Se déconnecter</Bouton>
    </main>
  );
}

/**
 * Attente du fragment. Un texte neutre, pas un squelette animé : sur écran bas
 * de gamme, l'animation coûte des images par seconde pour rien.
 */
function AttenteFragment({ children }: { children: ReactNode }) {
  return <Suspense fallback={<p className="sygap-chargement">Chargement…</p>}>{children}</Suspense>;
}

/**
 * Choisit la surface.
 *
 * Trois routes n'appartiennent qu'au portail. `mot-de-passe`, lui, est
 * **partagé** : tout compte doit pouvoir changer son mot de passe, et c'est
 * alors le public de l'agent — non l'adresse — qui désigne la surface. Le
 * rendre systématiquement au poste agent donnerait à un patron de pirogue la
 * barre latérale des administrations.
 */
function estSurfacePortail(racine: string | undefined, session: Session): boolean {
  if (
    racine === 'mes-titres' ||
    racine === 'mes-dossiers' ||
    racine === 'deposer' ||
    racine === 'mes-redevances' ||
    racine === 'mes-marees'
  ) {
    return true;
  }
  // `mes-notifications` et `mes-preferences` rejoignent `mot-de-passe` dans le
  // même cas : tout compte y accède, et c'est le **public** — non l'adresse —
  // qui désigne la surface. Les rendre systématiquement au poste agent
  // donnerait à un patron de pirogue la barre latérale des administrations.
  if (racine === 'mot-de-passe' || racine === 'mes-notifications' || racine === 'mes-preferences') {
    return estProfessionnelSeul(session);
  }
  return false;
}

export function PageIntrouvable({ accueil }: { accueil: string }) {
  return (
    <>
      <h1 className="sygap-titre-page">Page introuvable</h1>
      <Message ton="information" titre="Cette adresse ne correspond à aucun écran de SYGAP.">
        <Lien href={accueil}>Revenir à l’accueil</Lien>
      </Message>
    </>
  );
}
